import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BHPromptWorkshopAppointmentsView } from '@adornis/bh-registration/client/bh-prompt-appointments-view.js';
import { book } from '@adornis/buchungsheld-db/api/booking.js';
import { generateMultiCertificate } from '@adornis/buchungsheld-db/api/multiCertificate.js';
import {
  getParticipationPayment,
  subscribeParticipationsActiveByUser,
} from '@adornis/buchungsheld-db/api/participation.js';
import type { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { GlobalSetting } from '@adornis/buchungsheld-db/entities/global-setting.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { ParticipationEinzelberatung } from '@adornis/buchungsheld-db/entities/ParticipationEinzelberatung.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import {
  RegistrationData,
  RegistrationEinzelberatungData,
  RegistrationWorkshopData,
} from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { BlockSignoffDaysInAdvanceError } from '@adornis/buchungsheld-db/errors/BlockSignoffDaysInAdvanceError.js';
import { TimeslotsAreoverlappingError } from '@adornis/buchungsheld-db/errors/TimeslotsAreoverlappingError.js';
import { formatPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import { nachrueckPossible } from '@adornis/buchungsheld-db/logic/participation.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/cms/client/generated-form.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import type { Payment } from '@adornis/payments/db/payments.js';
import type { Price } from '@adornis/payments/db/prices.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, map, of, switchMap } from 'rxjs';
import '../../components/bh-share.js';
import { BHPromptWorkshopDocumentsView } from '../../prompts/bh-prompt-documents-view.js';
import { BHPromptFinalizePayment } from '../../prompts/bh-prompt-finalize-payment.js';
import { BHPromptGenerateGlobalCertificate } from '../../prompts/bh-prompt-generate-global-certificate.js';
import { type SocialShareOptions } from '../../social-share.js';

function triggerSendMailAction(emailAddress: string, subject?: string, body?: string) {
  let mailtoLink = `mailto:${emailAddress}`;

  if (subject) {
    mailtoLink += `?subject=${encodeURIComponent(subject)}`;
  }

  if (body) {
    if (subject) {
      mailtoLink += `&body=${encodeURIComponent(body)}`;
    } else {
      mailtoLink += `?body=${encodeURIComponent(body)}`;
    }
  }

  window.location.href = mailtoLink;
}

@customElement('bh-page-profile-events-participation')
export class BHProfileEventsParticipation extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);
  @state() private readonly _size = new WindowSizeController(this);

  @state() private readonly _user = new RXController(this, currentUser<User>());
  @state() private readonly _participations = new RXController(
    this,
    this._user.observable.pipe(
      filter(Boolean),
      switchMap(user =>
        subscribeParticipationsActiveByUser(user._id)({
          _id: 1,
          _class: 1,
          name: 1,
          state: 1,
          userID: 1,
          productID: 1,
          waitingListPlace: 1,
          product: {
            name: 1,
            isCanceled: 1,
            institutionID: 1,
            institution: { settings: { replyToMailAddress: 1 } },
            appointments: { start: 1, end: 1, name: 1, location: 1 },
            slotConfig: { start: 1, end: 1 },
            documents: { _id: 1, name: 1 },
            timeframeID: 1,
            timeframe: { name: 1 },
            hasPrice: 1,
            prices: { _id: 1, unitAmount: 1, currency: 1 },
            leftCapacity: 1,
            settings: {
              blockAutomaticMoveUpDaysInAdvance: 1,
              inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
            },
          },
          nachrueckPossible: 1,
        }),
      ),
      map(it => it as Participation[]),
      map(participations => {
        return participations.toSorted((a, b) => {
          const a_start =
            (a.product as Workshop).appointments?.at(0)?.start || (a.product as Einzelberatung).slotConfig?.start;
          const b_start =
            (b.product as Workshop).appointments?.at(0)?.start || (b.product as Einzelberatung).slotConfig?.start;
          console.log({ a_start, b_start });
          if (!a_start && !b_start) return 0;
          if (!a_start) return -1;
          if (!b_start) return 1;
          return a_start.toMillis() - b_start.toMillis();
        });
      }),
    ),
  );

  @state() private readonly _participationsPrices = new RXController(
    this,
    this._participations.observable.pipe(
      filter(Boolean),
      switchMap(async participations => {
        const result = [] as { participationID: string; price: Price }[];
        for (const participation of participations) {
          const price = await participation.getPriceForUser();
          if (!price) continue;
          result.push({ participationID: participation._id, price });
        }
        return result;
      }),
    ),
  );

  @state() private readonly payments = new RXController(
    this,
    this._participations.observable.pipe(
      filter(Boolean),
      switchMap(async participations => {
        const getPayment = async (id: string) => {
          const payment = (await getParticipationPayment(id, true)()) as [string, number] | undefined;
          if (!payment) return undefined;
          return [id, { status: payment[0], amount: payment[1] * 1 }] as const;
        };
        const payments$ = participations.map(participation => getPayment(participation._id));
        const payments = await Promise.all(payments$);
        return payments
          .filter(result => result && isDefined(result[1]))
          .reduce<Record<string, Payment>>(
            (acc, [participationId, payment]) => ({ ...acc, [participationId]: payment }),
            {},
          );
      }),
    ),
  );

  @state() private readonly globalSettings = new RXController(
    this,
    GlobalSetting.getGlobalSetting()({ multiCertTemplateID: 1 }),
  );

  private readonly _userInstitutions = new RXController(
    this,
    combineLatest([this._user.observable, this._participations.observable]).pipe(
      filter(Boolean),
      switchMap(([user, participations]) => {
        if (!user) return of([]);
        const institutionPromises = Array.from(
          new Set(participations?.map(p => p.product?.institutionID).filter(Boolean)),
        ).map(id => Institution.getByID<Institution>(id as string)(Institution.allFields));
        return Promise.all(institutionPromises);
      }),
    ),
  );

  private readonly _userInstitutionsStamdaten = combineLatest([
    this._user.observable,
    this._userInstitutions.observable,
  ]).pipe(
    switchMap(([user, institutions]) => {
      if (!user) return [];

      const stammdatenPromises = (institutions ?? []).filter(isDefined).map(institution =>
        UserStammdaten.getByUserAndInstitution(
          user._id,
          institution._id,
        )(UserStammdaten.allFields).then(stammdaten => {
          return { institution, stammdaten };
        }),
      );
      return Promise.all(stammdatenPromises).then(data => data.filter(isDefined));
    }),
  );

  @state() private readonly _stammdatenControllers = new RXController(
    this,
    this._userInstitutionsStamdaten.pipe(
      map(data => {
        return data.map(({ institution, stammdaten }) => ({
          institution,
          stammdatenController: new GenericFieldFormController(this, stammdaten, {
            success: async stammdaten => {
              await XDialog.waitFor(stammdaten.save());
              void XSnackbar.show(this._translation.translate('PROFILE', 'SUCCESS_SAVING_PROFILE'));
              this.requestUpdate();
            },
          }),
        }));
      }),
    ),
  );

  override render() {
    if (this._participations.isLoading || this._userInstitutions.isLoading || this._stammdatenControllers.isLoading)
      return html` <x-flex flex center crossaxis-center> <tr-loader></tr-loader> </x-flex> `;

    return html`
      <x-flex space="lg">
        ${this._renderCalenderLinkField()}

        ${
          this.globalSettings.value?.multiCertTemplateID &&
          !window.location.hostname.includes('uni-frankfurt') &&
          !window.location.hostname.includes('buchungsheld.staging.adornis.de')
            ? html`<x-button @click=${() => this.generateGlobalCertificate()}
                >${this._translation.translate('PROFILE', 'generate_global_certificate')}</x-button
              >`
            : nothing
        }
          
        </x-basic-card>

        <x-flex horizontal space="md" space-between crossaxis-center wrap>
          <x-h1> ${this._translation.translate('WORKSHOP_OVERVIEW')} </x-h1>
          <tr-button mode="outline" icon="add" @click=${() => {
            let event = new CustomEvent('onGoToWorkshopOffers', { detail: {}, composed: true, bubbles: true });
            this.dispatchEvent(event);
          }}>
            ${this._translation.translate('SIGNUP_TO_WORKSHOPS')}
          </tr-button>
        </x-flex>

        ${this._renderEventList()}
      </x-flex>
    `;
  }
  async generateGlobalCertificate() {
    const participationIDs = await BHPromptGenerateGlobalCertificate.showPopup();
    if (participationIDs) {
      const fileID = await XDialog.waitFor(generateMultiCertificate(participationIDs)());
      const link = AdornisFile.getServeLink(fileID);
      goTo(link + '/gesamt-cert.pdf', { target: '_blank' });
    }
  }

  private _renderCalenderLinkField() {
    const calendarlink = `${baseConfig.get('ROOT_URL')}calendar/${this._user.value?._id}.ics`;

    return html`
      <tr-flex vertical crossaxis-center space="xs" wrap ${css({ alignItems: 'start' })}>
        <tr-text>${this._translation.translate('PROFILE', 'icsLinkNote')}</tr-text>
        <tr-flex horizontal space="xs" crossaxis-center ${css({ width: '100%' })}>
          <tr-input
            flex
            ${css({ cursor: 'pointer', width: '100%', flexGrow: '1' })}
            disabled
            .value=${calendarlink}
            icon="content_copy"
            @click=${() => {
              void navigator.clipboard.writeText(calendarlink);
              return XSnackbar.show(this._translation.translate('copiedLink'));
            }}
          ></tr-input>
          <tr-icon
            ${acss({ cursor: 'pointer', opacity: '0.5', '&:hover': { opacity: '.7' } })}
            @click=${() =>
              XDialog.showPopup({
                content: () => html` <tr-flex ${css({ padding: '40px' })}>
                  <h2>Anleitung um einen Kalender per ICS zu abonieren.</h2>
                  ${getExplanations(globalLanguageSubject.value).map((explanation, index) => {
                    return html`
                      <tr-toggle-content .open=${index === 0} .label=${explanation.title}>
                        <ol>
                          ${explanation.steps.map(step => html` <li>${step.text}</li> `)}
                        </ol>
                      </tr-toggle-content>
                    `;
                  })}
                </tr-flex>`,
              })}
            >info</tr-icon
          >
        </tr-flex>
      </tr-flex>
    `;
  }

  private _renderParticipation(
    participation: NarrowedEntity<
      Participation,
      {
        _id: 1;
        state: 1;
        userID: 1;
        nachrueckPriority: 1;
        waitingListPlace: 1;
        product: {
          institutionID: 1;
          isCanceled: 1;
          leftCapacity: 1;
          nachrueckBlockTimeForLowPriorityInDays: 1;
          appointments: { start: 1 };
          settings: {
            blockAutomaticMoveUpDaysInAdvance: 1;
            inheritedBlockAutomaticMoveUpDaysInAdvance: 1;
          };
        };
      }
    >,
  ) {
    const product = participation.product;
    if (!product) return nothing;

    const renderProduct = (product: Product) => {
      if (product instanceof Workshop) return this._renderWorkshop(participation, product);
      if (product instanceof Einzelberatung) return this._renderCounseling(participation, product);
    };

    const user = this._user.value;
    if (!user) throw new Error('User not found');
    const price = this._participationsPrices.value?.find(p => p.participationID === participation._id)?.price;

    const entry = html`
      <x-flex
        ${acss({
          display: 'contents',
          ...(product.isCanceled
            ? {
                // deactivate when product is cancled
                ' > *': {
                  pointerEvents: 'none',
                  filter: 'grayscale(100%)',
                  opacity: '0.5',
                },
              }
            : {}),
        })}
      >
        ${renderProduct(product)}
        ${(() => {
          if (product.isCanceled)
            return html`
              <tr-flex
                padding="md"
                center
                crossaxis-center
                ${css({
                  background: this.colors.tone.success.shade(200),
                  color: this.colors.tone.black,
                  borderRadius: this.sizes.borderRadius,
                })}
              >
                <tr-text> ${this._translation.translate('PRODUCT', 'canceled')} </tr-text>
              </tr-flex>
            `;

          if (participation.state === ParticipationState.PARTICIPATED)
            return html`
              <tr-flex
                padding="md"
                center
                crossaxis-center
                ${css({
                  background: this.colors.tone.success.shade(200),
                  color: this.colors.tone.black,
                  borderRadius: this.sizes.borderRadius,
                })}
              >
                <tr-text> ${this._translation.translate('field', 'participated')} </tr-text>
              </tr-flex>
            `;

          return html`
            <x-flex space="sm" flex>
              <tr-card flex ${css({ width: '100%' })}>
                <x-flex space="sm" w100 h100 center crossaxis-center>
                  <x-text>
                    ${this._translation.translate('WORKSHOPS', 'STATES', participation.state)}
                    ${participation.state === ParticipationState.WAITING
                      ? `(${this._translation.translate('place')}: ${participation.waitingListPlace})`
                      : nothing}
                  </x-text>
                </x-flex>
              </tr-card>

              ${nachrueckPossible(participation)
                ? html`
                    <tr-button
                      mode="action"
                      icon="move_up"
                      tone="warning"
                      ${css({ width: '100%', background: this.colors.tone.warning.opacity(0.5) })}
                      @click=${async () => {
                        try {
                          if (!price || price.unitAmount === 0) {
                            // free
                            await XDialog.waitFor(
                              RegistrationData.moveUpFromWaitinglistIntoParticipating(participation._id, null)({}),
                            );
                          } else {
                            // user has to pay
                            await BHPromptFinalizePayment.showPopup({
                              props: { participationID: participation._id },
                            });
                          }

                          this.requestUpdate();
                        } catch (err) {
                          console.error(err);
                          XSnackbar.show(this._translation.translate('err', 'something_went_wrong'));
                        }
                      }}
                    >
                      ${this._translation.translate('user', 'claim_waitinglist_spot')}
                    </tr-button>
                  `
                : nothing}
            </x-flex>
          `;
        })()}

        <!-- action to edit participation -->
        ${this._participationStateAction(participation)}
      </x-flex>
    `;
    if (!this._size.mobile)
      return html`
        <!-- workshop / appointment view -->
        ${entry}
      `;

    return html` <x-flex space="sm"> ${entry} </x-flex> `;
  }
  private _renderEventList() {
    const participations = this._participations.value ?? [];

    if (participations.length === 0)
      return html`
        <x-empty-placeholder
          no-wrapper
          .text=${this._translation.translate('PROFILE', 'NO_WORKSHOPS')}
        ></x-empty-placeholder>
      `;

    return html`
      <x-grid
        space=${!this._size.mobile ? 'sm' : 'md'}
        columns="1fr ${!this._size.mobile ? 'max-content max-content' : ''}"
      >
        ${!this._size.mobile
          ? html`
              <x-text>${this._translation.translate('PROFILE', 'pat_desc')}</x-text>
              <x-text>${this._translation.translate('PROFILE', 'pat_status')}</x-text>
              <x-text>${this._translation.translate('PROFILE', 'pat_change_status')}</x-text>
            `
          : nothing}
        ${repeat(participations, (participation, i) => this._renderParticipation(participation))}
      </x-grid>
    `;
  }

  private _renderCard(content1: any, content2: any) {
    return html`
      <tr-card participation-card flex ${css({ width: '100%' })}>
        <x-flex
          horizontal
          space="sm"
          space-between
          ${css({
            flexDirection: (this._size.width ?? 0) < 450 ? 'column' : 'row',
          })}
        >
          <x-flex space=${(this._size.width ?? 0) < 450 ? 'sm' : 'xs'} flex> ${content1} </x-flex>
          <x-flex space="md" space-between> ${content2} </x-flex>
        </x-flex>
      </tr-card>
    `;
  }

  private _renderIsPaidButton(paymentStatus: string) {
    const { tr, color } =
      paymentStatus === 'paid'
        ? { tr: 'paid' as const, color: this.colors.tone.success.shade(700) }
        : { tr: 'not_paid' as const, color: this.colors.secondary.shade(700) };

    return html`<tr-button-small not-clickable .color=${color} style="white-space: nowrap">
      <x-text>${this._translation.translate('PROFILE', tr)}</x-text>
    </tr-button-small>`;
  }

  private _renderCounseling(participation: ParticipationEinzelberatung, counseling: Einzelberatung) {
    const timeslot = counseling.slots.find(slot => slot._id === participation.timeslotID);
    const payment = this.payments.value?.[participation._id];
    const counselingTime = (() => {
      const start = timeslot?.start.toLocaleString(DateTime.DATETIME_MED);
      const end = timeslot?.end.toFormat('HH:mm');
      if (start && end) return start + ' - ' + end;
      return '';
    })();

    if (!timeslot) return nothing;

    const content1 = html`
      <x-h3 bold>${counseling.name}</x-h3>
      <x-text>
        ${this._translation.translate('PROFILE', 'single_appointment_planned_for')}
        <x-text bold ${css({ display: 'inline-block' })}> ${counselingTime} </x-text>
        ${counseling.infoLink
          ? html`
              in
              ${counseling.infoLink.startsWith('http')
                ? html`
                    <link ${css({ display: 'inline-block' })} href=${counseling.infoLink}>
                      <x-text bold> ${counseling.infoLink} </x-text>
                    </link>
                  `
                : html`
                        <x-text bold ${css({ display: 'inline-block' })}></x-text>
                          ${counseling.infoLink}
                        </x-text>
                        `}
            `
          : nothing}
      </x-text>
      <x-text>
        ${this._userInstitutions.value?.find(inst => inst?._id === counseling.institutionID)?.name ?? ''}
      </x-text>
      <x-text> ${counseling.timeframe?.name ?? ''} </x-text>
    `;

    const content2 = html`
      ${counseling.isPaymentNeeded && payment
        ? html`
            <x-flex horizontal space="xs">
              ${this._renderIsPaidButton(payment.status)}
              <tr-button-small not-clickable .color=${this.colors.accent.shade(700)}>
                <x-text>${formatPrice(payment.amount)}</x-text>
              </tr-button-small>
            </x-flex>
          `
        : nothing}
    `;

    return this._renderCard(content1, content2);
  }

  private _renderWorkshop(participation: Participation, workshop: Workshop) {
    const hasAppointment = workshop.appointments.length > 0;
    const nextAppointment = () => {
      const appointments = workshop.appointments;
      let chosenAppointment: Appointment | null = null;

      for (let i = 0; i < appointments.length; i++) {
        if (chosenAppointment && !chosenAppointment.start) throw new Error('unreachable');
        const appointment = appointments[i];
        if (!appointment?.start) continue;
        if (appointment.start < DateTime.now()) continue;
        if (!chosenAppointment || appointment.start < chosenAppointment.start!) {
          chosenAppointment = appointment;
          continue;
        }
      }

      return chosenAppointment;
    };

    const appointment = nextAppointment();
    const appointmentTime = (() => {
      const start = appointment?.start?.toLocaleString(DateTime.DATETIME_MED);
      const end = appointment?.end?.toFormat('HH:mm');
      if (!start) return 'TBD';
      if (start && end) return start + ' - ' + end;
      if (start) return start;
      throw new Error('unreachable');
    })();
    const appointmentLocation = appointment?.location;
    const payment = this.payments.value?.[participation._id];

    const actions = [
      {
        label: this._translation.translate('PROFILE', 'all_appointments'),
        icon: 'calendar_month',
        onClick: (): Promise<void> | void => {
          return BHPromptWorkshopAppointmentsView.showPopup({
            props: { hasCloseButton: true, workshop },
          });
        },
      },
    ];

    const contactMail = workshop.institution?.settings.replyToMailAddress;
    if (contactMail) {
      const workshopStart = workshop.start;
      actions.push({
        label: this._translation.translate('contact'),
        icon: 'mail',
        onClick: () => {
          let subject = workshop.name;
          if (workshopStart) subject += ' ' + workshopStart.toFormat('dd.MM.yyyy', { locale: 'de' });
          triggerSendMailAction(contactMail, subject);
        },
      });
    }

    if (workshop.documents?.length ?? 0 > 0)
      actions.push({
        label: this._translation.translate('PROFILE', 'all_documents'),
        icon: 'description',
        onClick: () => {
          return BHPromptWorkshopDocumentsView.showPopup({
            props: { hasCloseButton: true, workshopID: workshop._id },
          });
        },
      });

    const content1 = html`
      <x-h3 bold ${css({ marginBottom: this.spacing.xs })}>${workshop.name}</x-h3>
      ${hasAppointment
        ? appointment === null
          ? html`<x-text>${this._translation.translate('PROFILE', 'no_events')}</x-text>`
          : html`<x-text>
              ${this._translation.translate('PROFILE', 'next_termin')}
              <x-text bold ${css({ display: 'inline-block' })}>
                ${appointmentTime}
                ${appointmentLocation
                  ? html`
                      in
                      ${appointmentLocation.startsWith('http')
                        ? html`
                            <link ${css({ display: 'inline-block' })} href=${appointmentLocation}>
                              <x-text bold> ${appointmentLocation} </x-text>
                            </link>
                          `
                        : html` <x-text bold ${css({ display: 'inline-block' })}> ${appointmentLocation} </x-text> `}
                    `
                  : nothing}
              </x-text>
            </x-text> `
        : html` <x-text>${this._translation.translate('PROFILE', 'no_workshops')}</x-text> `}

      <x-text> ${this._userInstitutions.value?.find(inst => inst?._id === workshop.institutionID)?.name ?? ''} </x-text>
      <x-text> ${workshop.timeframe?.name ?? ''} </x-text>
      <tr-flex crossaxis-start horizontal space="xs" ${css({ flexWrap: 'wrap' })}>
        ${actions.map(
          action => html`
            <tr-button
              mode="outline"
              tone="subtle"
              icon=${action.icon}
              @click=${action.onClick}
              ${css({ marginTop: this.spacing.sm })}
            >
              ${action.label}
            </tr-button>
          `,
        )}
      </tr-flex>
    `;

    const reference = createRef<XDropdown>();
    const content2 = html`
      <tr-dropdown share-btn ${ref(reference)}>
        <tr-chip @click=${() => reference.value?.toggle()}>
          <x-flex horizontal center crossaxis-center>
            <tr-icon>share</tr-icon>
            <tr-text>Teilen</tr-text>
          </x-flex>
        </tr-chip>

        <tr-basic-card slot="dropdown-content">
          <x-flex horizontal space="sm">
            <bh-share
              only-icons
              .iconSize=${this._size.mobile ? '2.75em' : '2.25em'}
              .shareOptions=${{
                content: this._translation.translate('share', 'content'),
                url: window.location.href,
                hashtags: ['#buchungsheld'],
              } as SocialShareOptions}
            ></bh-share>
          </x-flex>
        </tr-basic-card>
      </tr-dropdown>

      ${workshop.isPaymentNeeded && payment
        ? html`
            <x-hr></x-hr>
            <x-flex space="xs">
              ${this._renderIsPaidButton(payment.status)}
              <tr-button-small not-clickable .color=${this.colors.accent.shade(700)} ${css({ whiteSpace: 'nowrap' })}>
                <x-text>${formatPrice(payment.amount)}</x-text>
              </tr-button-small>
            </x-flex>
          `
        : nothing}
    `;

    return this._renderCard(content1, content2);
  }

  private _participationStateAction(participation: Participation) {
    if (participation.state === ParticipationState.PARTICIPATED) {
      if (!this._size.mobile) return html` <tr-flex> </tr-flex> `;
      return nothing;
    }

    let text = '';
    let action = async () => {};
    let color = '';

    switch (participation.state) {
      // eslint-disable-next-line default-case-last
      default:
      case ParticipationState.LEFT:
        color = this.colors.accent;
        text = this._translation.translate('PROFILE', 'book');
        action = async () => this.reregisterToProduct(participation);
        break;
      case ParticipationState.IN:
      case ParticipationState.WAITING:
        color = this.colors.tone.error;
        text = this._translation.translate('PROFILE', 'cancle_booking');
        action = async () => this.cancelProductEnrollment(participation);
        break;
      case ParticipationState.SUSPENDED:
        color = this.colors.tone.error;
        text = 'Keine Aktion möglich';
        break;
    }

    return html`
      <x-flex
        padding="md"
        center
        crossaxis-center
        ${css({
          cursor: 'pointer',
          background: color,
          color: this.colors.on.tone.error,
          borderRadius: this.sizes.borderRadius,
        })}
        @click=${async () => {
          try {
            await action();
          } catch (error: any) {
            const errClass = (error as AdornisEntity | undefined)?._class;
            if (!errClass) return;

            if (errClass === BlockSignoffDaysInAdvanceError._class && error.extensions.translationKey) {
              void XSnackbar.show(this._translation.translate(error.extensions.translationKey));
            }

            participation.revert();
          }
        }}
      >
        <x-text> ${text} </x-text>
      </x-flex>
    `;
  }

  /** Attentions! - you need to catch errors outside of this function */
  async cancelProductEnrollment(participation: Participation) {
    const participationID = participation._id;
    await XDialog.waitFor(Participation.cancelEnrollment(participationID, false)());
    this.requestUpdate();
  }

  async reregisterToProduct(participation: Participation) {
    try {
      const productData = (() => {
        if (participation instanceof ParticipationEinzelberatung) {
          return [
            new RegistrationEinzelberatungData({
              productID: participation.productID,
              timeslotID: participation.timeslotID,
            }),
          ];
        } else {
          return [new RegistrationWorkshopData({ productID: participation.productID })];
        }
      })();

      const product = participation.product;
      if (!product) throw new Error('price not found');
      if (!product.hasPrice) {
        // free
        await XDialog.waitFor(book(productData, null)({}));
      } else {
        // user has to pay
        await BHPromptFinalizePayment.showPopup({
          props: { participationID: participation._id },
        });
      }

      this.requestUpdate();
    } catch (err: any) {
      console.error(err);
      if (err instanceof TimeslotsAreoverlappingError)
        return void XSnackbar.show(this._translation.translate('REGISTRATION', 'cannotSelectAppointmentCollision'));

      void XSnackbar.show(err?.message ?? this._translation.translate('err', 'unexpected'));
    }
  }

  override styles(): Styles[] {
    const isSmViewPort = (this._size.width ?? 0) < 450;
    return [
      ...super.styles(),
      {
        '[participation-card]': {
          position: 'relative',

          '[share-btn]': {
            cursor: 'pointer',
            ...(isSmViewPort
              ? {
                  position: 'absolute',
                  right: this.spacing.xs,
                  top: this.spacing.xs,
                }
              : {}),

            'tr-chip': {
              overflow: 'hidden',
              background: this.colors.light,
              color: this.colors.black,
              padding: `${this.baseFontSize}px`,

              ...(!isSmViewPort
                ? {
                    'tr-icon': {
                      padding: '6px 6px 7px 10px',
                      marginTop: '-6px',
                      marginLeft: '-13px',
                      marginBottom: '-5px',
                    },
                  }
                : {}),

              'tr-text': isSmViewPort ? { display: 'none' } : {},
            },
            '> [slot]': {
              background: this.colors.light,
            },
          },

          ...(isSmViewPort
            ? {
                'x-h3': {
                  paddingRight: this.spacing.lg,
                },
              }
            : {}),
        },
      },
    ];
  }
}

function getExplanations(lang) {
  if (lang === 'de')
    return [
      {
        title: 'Google Kalendar',
        steps: [
          { text: 'Öffne deinen Google Kalender in einem Webbrowser.' },
          {
            text: 'In der linken Seitenleiste siehst du eine Liste mit Kalendern. Klicke auf das Pluszeichen (+) neben „Weitere Kalender“.',
          },
          { text: 'Wähle die Option „Über URL hinzufügen“ aus.' },
          { text: 'Füge den ICS-Link in das Feld „URL des Kalenders“ ein.' },
          { text: 'Klicke auf „Kalender hinzufügen“ und der Kalender wird zu deinem Google Kalender hinzugefügt.' },
        ],
      },
      {
        title: 'Microsoft Outlook',
        steps: [
          { text: 'Öffne Outlook in einem Webbrowser oder der Desktopanwendung.' },
          { text: 'Klicke auf „Kalender“ in der unteren Symbolleiste, um den Kalender zu öffnen.' },
          { text: 'Klicke oben auf „Kalender hinzufügen“ und wähle „Aus dem Internet“ aus.' },
          { text: 'Füge den ICS-Link in das Feld „Link zum Kalender“ ein.' },
          { text: 'Klicke auf „Speichern“ und der Kalender wird zu deinem Outlook-Kalender hinzugefügt.' },
        ],
      },
      {
        title: 'Apple Kalender (macOS)',
        steps: [
          { text: 'Öffne den Apple Kalender auf deinem Mac.' },
          { text: 'Klicke im Menü „Ablage“ auf „Neues Kalenderabonnement“.' },
          { text: 'Füge den ICS-Link in das Feld „URL“ ein.' },
          { text: 'Klicke auf „Abonnieren“ und passe gegebenenfalls die Einstellungen an.' },
          { text: 'Klicke auf „OK“ und der Kalender wird zu deinem Apple Kalender hinzugefügt.' },
        ],
      },
      {
        title: 'Apple Kalender (iOS)',
        steps: [
          { text: 'Kopiere den ICS-Link auf deinem iOS-Gerät.' },
          { text: 'Gehe zu „Einstellungen“ und tippe auf „Kalender“.' },
          { text: 'Wähle „Account hinzufügen“ und dann „Andere“.' },
          { text: 'Wähle „Kalender hinzufügen“ und füge den ICS-Link in das Feld „Server“ ein.' },
          { text: 'Tippe auf „Weiter“ und passe gegebenenfalls die Einstellungen an.' },
          { text: 'Tippe auf „Sichern“ und der Kalender wird zu deinem Apple Kalender hinzugefügt.' },
        ],
      },
    ];

  return [
    {
      title: 'Google Calendar',
      steps: [
        { text: 'Open your Google Calendar in a web browser.' },
        {
          text: 'In the left sidebar, you will see a list of calendars. Click the plus sign (+) next to "Other Calendars".',
        },
        { text: 'Select the "Add by URL" option.' },
        { text: 'Paste the ICS link into the "URL of the calendar" field.' },
        { text: 'Click "Add Calendar" and the calendar will be added to your Google Calendar.' },
      ],
    },
    {
      title: 'Microsoft Outlook',
      steps: [
        { text: 'Open Outlook in a web browser or the desktop application.' },
        { text: 'Click "Calendar" in the lower symbol bar to open the calendar.' },
        { text: 'Click "Add Calendar" at the top and select "From the Internet".' },
        { text: 'Paste the ICS link into the "Link to calendar" field.' },
        { text: 'Click "Save" and the calendar will be added to your Outlook calendar.' },
      ],
    },
    {
      title: 'Apple Calendar (macOS)',
      steps: [
        { text: 'Open the Apple Calendar on your Mac.' },
        { text: 'Click the "Archive" menu and select "New Calendar Subscription".' },
        { text: 'Paste the ICS link into the "URL" field.' },
        { text: 'Click "Subscribe" and adjust settings, if necessary.' },
        { text: 'Click "OK" and the calendar will be added to your Apple Calendar.' },
      ],
    },
    {
      title: 'Apple Calendar (iOS)',
      steps: [
        { text: 'Copy the ICS link on your iOS device.' },
        { text: 'Go to "Settings" and tap "Calendar".' },
        { text: 'Select "Add Account" and then "Other".' },
        { text: 'Select "Add Calendar" and paste the ICS link into the "Server" field.' },
        { text: 'Tap "Continue" and adjust settings, if necessary.' },
        { text: 'Tap "Save" and the calendar will be added to your Apple Calendar.' },
      ],
    },
  ];
}
